import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../../../foundation/altzero_base/base/components/ui/button";
import { Input } from "../../../foundation/altzero_base/base/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../foundation/altzero_base/base/components/ui/card";

import { ResponsiveTable } from "../components/ResponsiveTable";
import { Pagination } from "../components/Pagination";
import { useToast } from "../../../foundation/altzero_base/base/hooks/use-toast";
import {
  FileText,
  Search,
  Edit,
  Trash2,
  ArrowLeft,
  Building2,
  Eye,
  MapPin,
  Zap,
  DollarSign,
  Play,
} from "lucide-react";
import ScopingAILayout from "../components/ScopingAILayout";
import { getTenders, deleteTender, Tender } from "../services/tenderService";
import { Badge } from "../../../foundation/altzero_base/base/components/ui/badge";
import { supabase } from "../../../foundation/altzero_base/base/utils/supabaseClient";
import { RetailerResponseCell } from "../components/RetailerResponseCell";
import { usePermissions } from "../../../foundation/altzero_base/base/contextapi/PermissionsContext";
import TenderSitesOverview from "../components/TenderSitesOverview";
import SiteOffersView from "../components/SiteOffersView";

interface TenderDetails {
  tender: Tender;
  sites: any[];
  offers: any[];
}

type DialogView = 'sites' | 'offers';

interface TenderSite {
  id: string;
  name: string;
  nmi: string;
  addr_street?: string;
  addr_city?: string;
  addr_state?: string;
  addr_postcode?: string;
  addr_country?: string;
  annual_kwh_peak?: number;
  annual_kwh_offpeak?: number;
  annual_kwh_shoulder?: number;
  abn?: string;
  acn?: string;
  due_at?: string;
  current_contract_expiry?: string;
  current_meter_provider?: string;
  current_retailer?: string;
  dma_provider?: string;
  retailer_comms?: string;
  create_dma?: boolean;
  request_meter_data?: boolean;
  request_bills?: boolean;
  acquire_ced?: boolean;
}

export default function TendersManagement() {
  const PAGE_SIZE = 10;

  const { toast } = useToast();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [tenders, setTenders] = useState<Tender[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedTender, setSelectedTender] = useState<TenderDetails | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [dialogView, setDialogView] = useState<DialogView>('sites');
  const [selectedSite, setSelectedSite] = useState<TenderSite | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Tender | null>(null);
  const [deleting, setDeleting] = useState(false);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    loadTenders(1, "");
  }, []);

  const loadTenders = async (page: number, search: string) => {
    setLoading(true);
    const result = await getTenders(page, PAGE_SIZE, search);
    if (result.success && result.data) {
      setTenders(result.data);
      setTotalCount(result.totalCount ?? 0);
      setCurrentPage(page);
    } else {
      toast({
        title: "Error",
        description: result.error || "Failed to load tenders",
        variant: "destructive",
      });
    }
    setLoading(false);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => loadTenders(1, value), 350);
  };

  const handlePageChange = (page: number) => loadTenders(page, searchQuery);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const paginatedTenders = tenders;

  const handleViewDetails = async (tender: Tender) => {
    setLoadingDetails(true);
    setShowDetails(true);
    setDialogView('sites');
    setSelectedSite(null);

    try {
      // Fetch sites and offers in parallel
      const [
        { data: sites, error: sitesError },
        { data: offers, error: offersError },
      ] = await Promise.all([
        supabase
          .from("tender_site")
          .select(`
            *,
            site:site_id (
              document_file_paths,
              retailer_notes
            )
          `)
          .eq("tender_id", tender.id)
          .order("created_at", { ascending: true }),
        supabase
          .from("offer")
          .select(`
            *,
            retailer:retailer_id (
              id,
              name,
              logo
            )
          `)
          .eq("tender_id", tender.id)
          .order("created_at", { ascending: true }),
      ]);

      if (sitesError) throw sitesError;
      if (offersError) throw offersError;

      const mappedSites = (sites || []).map((site: any) => ({
        ...site,
        document_file_paths: site.site?.document_file_paths || null,
        retailer_notes: site.site?.retailer_notes || null,
      }));

      setSelectedTender({
        tender,
        sites: mappedSites,
        offers: offers || [],
      });
    } catch (error: any) {
      console.error("Error loading tender details:", error);
      toast({
        title: "Error",
        description: "Failed to load tender details",
        variant: "destructive",
      });
      setShowDetails(false);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleViewOffers = (site: TenderSite) => {
    setSelectedSite(site);
    setDialogView('offers');
  };

  const handleBackToSites = () => {
    setSelectedSite(null);
    setDialogView('sites');
  };

  const handleBackToTenders = () => {
    setSelectedTender(null);
    setShowDetails(false);
    setDialogView('sites');
    setSelectedSite(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    const result = await deleteTender(deleteTarget.id);
    setDeleting(false);
    setDeleteTarget(null);
    if (result.success) {
      toast({ title: "Tender deleted", description: `"${deleteTarget.opportunity_name}" has been deleted.` });
      loadTenders(currentPage, searchQuery);
    } else {
      toast({
        title: "Error",
        description: result.error || "Failed to delete tender",
        variant: "destructive",
      });
    }
  };

  const getStatusBadge = (status: string) => {
    const configs: Record<string, { variant: "default" | "secondary" | "destructive" | "outline"; color: string; label: string }> = {
      draft:                       { variant: "secondary",   color: "bg-gray-100 text-gray-700",    label: "Draft" },
      waiting_for_retailers_reply: { variant: "outline",     color: "bg-yellow-100 text-yellow-800", label: "Awaiting Replies" },
      retailers_replied:           { variant: "default",     color: "bg-teal-100 text-teal-800",    label: "Retailers Replied" },
      under_review:                { variant: "outline",     color: "bg-orange-100 text-orange-800", label: "Under Review" },
      tender_ready:                { variant: "default",     color: "bg-indigo-100 text-indigo-800", label: "Tender Ready" },
      sent_to_client:              { variant: "default",     color: "bg-blue-100 text-blue-800",    label: "Send to Client" },
      won:                         { variant: "default",     color: "bg-green-100 text-green-800",  label: "Won" },
      lost:                        { variant: "destructive", color: "bg-red-100 text-red-800",      label: "Loss" },
    };

    const config = configs[status] ?? { variant: "outline" as const, color: "bg-gray-100 text-gray-700", label: status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ') };

    return (
      <Badge variant={config.variant} className={config.color}>
        {config.label}
      </Badge>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const handleContinueTender = (tenderId: string) => {
    navigate(`/tender/documents/new?tender=${tenderId}`);
  };

  return (
    <ScopingAILayout>
      <div className="container mx-auto py-6 px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="mb-6 sm:mb-8">
          <Link
            to="/tender/resource-library"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-4 transition-colors"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Resource Library
          </Link>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 flex items-center gap-3">
                <FileText className="w-8 h-8 sm:w-10 sm:h-10 text-primary" />
                Tenders Management
              </h1>
              <p className="text-base sm:text-lg text-muted-foreground mt-2">
                View and manage all proposals/tenders
              </p>
            </div>
            {can('tender.create') && (
              <Button
                onClick={() => navigate('/tender/documents/new')}
                className="flex items-center gap-2"
              >
                <FileText className="w-4 h-4" />
                Create New Tender
              </Button>
            )}
          </div>
        </div>

        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-5 w-5" />
              <Input
                placeholder="Search tenders by name, client, or status..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10 h-12 text-base"
              />
            </div>
          </CardContent>
        </Card>

        {/* Tenders Table - Hide when showing details */}
        {!showDetails && (
          <Card>
            <CardHeader>
              <CardTitle>All Tenders ({totalCount})</CardTitle>
              <CardDescription>
                Proposals and tenders in your system
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">
                  Loading tenders...
                </div>
              ) : (
                <ResponsiveTable
                  data={paginatedTenders}
                  columns={[
                    {
                      key: "opportunity_name",
                      label: "Tender Name",
                      render: (tender) => (
                        <span className="font-medium">{tender.opportunity_name || "—"}</span>
                      ),
                    },
                    {
                      key: "client",
                      label: "Client",
                      render: (tender) => (
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                          {tender.client?.name || "—"}
                        </div>
                      ),
                    },
                    {
                      key: "status",
                      label: "Status",
                      render: (tender) => getStatusBadge(tender.status),
                    },
                    {
                      key: "retailer_stats",
                      label: "Retailer Response",
                      render: (tender) => <RetailerResponseCell tender={tender} />,
                    },
                    {
                      key: "created_at",
                      label: "Created",
                      render: (tender) => formatDate(tender.created_at),
                    },
                  ]}
                  actions={(tender) => (
                    <>
                      {can('tender.edit') && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleContinueTender(tender.id)}
                          title="Continue Tender Workflow"
                        >
                          <Play className="h-4 w-4 text-green-600" />
                        </Button>
                      )}
                      {can('tender.view') && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleViewDetails(tender)}
                          title="View Details"
                        >
                          <Eye className="h-4 w-4 text-primary" />
                        </Button>
                      )}
                      {can('tender.delete') && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteTarget(tender)}
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </>
                  )}
                  mobileCardRender={(tender) => {
                    const sent = tender.retailers_sent_count || 0;
                    const replied = tender.retailers_replied_count || 0;
                    const total = tender.retailers_total_count || 0;

                    return (
                      <div className="space-y-2">
                        <div className="font-medium text-lg">{tender.opportunity_name || "—"}</div>
                        <div className="flex items-center gap-2 text-sm">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                          {tender.client?.name || "—"}
                        </div>
                        <div className="flex items-center gap-2">
                          {getStatusBadge(tender.status)}
                        </div>
                        {(sent > 0 || total > 0) && (
                          <div className="text-sm text-muted-foreground">
                            Retailers: {replied}/{sent} replied
                            {total > 0 && ` (${total} total)`}
                          </div>
                        )}
                        <div className="text-sm text-muted-foreground">
                          Created: {formatDate(tender.created_at)}
                        </div>
                      </div>
                    );
                  }}
                  emptyMessage="No tenders found."
                />
              )}
              {totalCount > 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  itemsPerPage={PAGE_SIZE}
                  totalItems={totalCount}
                />
              )}
            </CardContent>
          </Card>
        )}

        {/* Tender Details Inline View */}
        {showDetails && (
          <Card className="mt-6">
            <CardHeader className="border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  <CardTitle>Tender Details</CardTitle>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleBackToTenders}
                  className="flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Tenders
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6">

              {loadingDetails ? (
                <div className="text-center py-8 text-muted-foreground">
                  Loading tender details...
                </div>
              ) : selectedTender ? (
                <div className="space-y-6">
                  {/* Tender Info Header */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">
                        {selectedTender.tender.opportunity_name || "Unnamed Tender"}
                      </CardTitle>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4" />
                          {selectedTender.tender.client?.name || "—"}
                        </div>
                        <div>{getStatusBadge(selectedTender.tender.status)}</div>
                        <div>Created: {formatDate(selectedTender.tender.created_at)}</div>
                      </div>
                    </CardHeader>
                  </Card>

                  {/* Dynamic Content Based on View */}
                  {dialogView === 'sites' ? (
                    <TenderSitesOverview
                      sites={selectedTender.sites}
                      offers={selectedTender.offers}
                      onViewOffers={handleViewOffers}
                      tenderId={selectedTender.tender.id}
                    />
                  ) : dialogView === 'offers' && selectedSite ? (
                    <SiteOffersView
                      site={selectedSite}
                      offers={selectedTender.offers}
                      onBack={handleBackToSites}
                    />
                  ) : null}

                </div>
              ) : null}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Delete Confirmation Modal — rendered via portal to escape any transform stacking context */}
      {deleteTarget && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => !deleting && setDeleteTarget(null)} />
          <div className="relative z-10 bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Tender</h3>
                <p className="mt-1 text-sm text-gray-600">
                  Are you sure you want to delete <span className="font-medium text-gray-900">"{deleteTarget.opportunity_name}"</span>?
                </p>
                <p className="mt-2 text-sm text-red-600">
                  This will permanently delete all associated sites, offers, emails, and documents. This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDeleteConfirm} disabled={deleting}>
                {deleting ? "Deleting..." : "Delete Tender"}
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </ScopingAILayout>
  );
}

